/**
 * 급번개 CTA — 시간대 / 요일에 따라 문구와 사진을 함께 고릅니다.
 *
 * 홈 배너와 FlashCreate 제목 placeholder 등 여러 곳에서 같은 카피를 씁니다.
 *
 * 문구 키 풀: locales/{ko,en,ja}.json 의 tone.flashCta.*
 *  - morning / noon / afternoon / eveningWeekday / eveningFriday / eveningWeekend
 *    / night / nightWeekend / midnight
 *  - morningWeekend / noonWeekend / afternoonWeekend
 *  - humorActive (진행중 번개가 있을 때 유머)
 *  - default (풀 비었을 때 fallback)
 *
 * 사진은 문구와 같은 시간대 판정에서 함께 나옵니다.
 * 따로 고르면 "심야 번개" 문구에 아침 사진이 붙는 식으로 언젠가 어긋납니다.
 * URL 은 앱이 이미 쓰고 있는 것만 재사용합니다 (새 에셋을 들이지 않음).
 */
import i18n from 'i18next';

export interface FlashCtaCopy {
    title: string;
    sub: string;
    /** 시간대에 맞는 배너 사진 */
    image?: string;
}

/** 문구 풀과 사진을 함께 고르기 위한 시간대 이름 */
type Slot =
    | 'morning' | 'noon' | 'afternoon'
    | 'evening' | 'night' | 'midnight'
    | 'active';

const IMG: Record<Slot, string> = {
    // 아침 — 브런치 카페
    morning: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=750&h=240&fit=crop',
    // 점심 — 밥상
    noon: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=750&h=240&fit=crop',
    // 오후 — 공원/피크닉
    afternoon: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=750&h=240&fit=crop',
    // 저녁 — 술자리
    evening: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=750&h=240&fit=crop',
    // 밤 — 와인
    night: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=750&h=240&fit=crop',
    // 심야 — 어두운 극장
    midnight: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=750&h=240&fit=crop',
    // 번개가 이미 돌고 있을 때 — 놀거리
    active: 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=750&h=240&fit=crop',
};

const tArr = (key: string): FlashCtaCopy[] => {
    const v = i18n.t(key, { returnObjects: true }) as any;
    return Array.isArray(v) ? v : [];
};

/** 지금이 어느 시간대인지 — 문구와 사진이 이 하나의 판정을 공유합니다 */
const resolveSlot = (hour: number): Exclude<Slot, 'active'> => {
    if (hour >= 6 && hour < 11) return 'morning';
    if (hour >= 11 && hour < 14) return 'noon';
    if (hour >= 14 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    if (hour >= 21 && hour < 24) return 'night';
    return 'midnight';
};

export const getFlashCTACopy = (opts?: { hasActiveFlash?: boolean }): FlashCtaCopy => {
    const now = new Date();
    const hour = now.getHours();
    const dow = now.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isFridayEvening = dow === 5 && hour >= 17;

    if (opts?.hasActiveFlash) {
        const humor = tArr('tone.flashCta.humorActive');
        if (humor.length > 0) {
            const pick = humor[Math.floor(Math.random() * humor.length)];
            return { ...pick, image: IMG.active };
        }
    }

    const slot = resolveSlot(hour);
    const pools: FlashCtaCopy[] = [];

    if (slot === 'morning') {
        pools.push(...tArr('tone.flashCta.morning'));
        if (isWeekend) pools.push(...tArr('tone.flashCta.morningWeekend'));
    } else if (slot === 'noon') {
        pools.push(...tArr('tone.flashCta.noon'));
        if (isWeekend) pools.push(...tArr('tone.flashCta.noonWeekend'));
    } else if (slot === 'afternoon') {
        pools.push(...tArr('tone.flashCta.afternoon'));
        if (isWeekend) pools.push(...tArr('tone.flashCta.afternoonWeekend'));
    } else if (slot === 'evening') {
        if (isFridayEvening) pools.push(...tArr('tone.flashCta.eveningFriday'));
        else if (isWeekend) pools.push(...tArr('tone.flashCta.eveningWeekend'));
        else pools.push(...tArr('tone.flashCta.eveningWeekday'));
    } else if (slot === 'night') {
        pools.push(...tArr('tone.flashCta.night'));
        if (isWeekend) pools.push(...tArr('tone.flashCta.nightWeekend'));
    } else {
        pools.push(...tArr('tone.flashCta.midnight'));
    }

    if (pools.length === 0) {
        const def = i18n.t('tone.flashCta.default', { returnObjects: true }) as any;
        return def && def.title
            ? { ...def, image: IMG[slot] }
            : { title: '', sub: '', image: IMG[slot] };
    }
    return { ...pools[Math.floor(Math.random() * pools.length)], image: IMG[slot] };
};
